import useNewsData from "@/src/UseData/useNewsData";
import React from "react";
import S from "./LatestStart.styled";
import Tag from "@/components/global-components/Tag";
import { MainSection } from "@/styles/global";
import Button from "@/components/global-components/Button";
import useEventsData, { IEvent } from "@/src/UseData/useEventsData";
import CalendarIcon from "@/assets/calendar.svg";
import ClockIcon from "@/assets/clock.svg";
import MapPinIcon from "@/assets/map-pin.svg";
import { formatDate, parseDate, toGatsbyImage } from "@/src/helpers";

export interface LatestStartProps {
  backgroundColors: string;
}

const LatestStart: React.FC<LatestStartProps> = ({ backgroundColors }) => {
  const newsData = useNewsData().slice(0, 3);

  const removeOldEvents = (events: IEvent[]) => {
    return events
      .filter((event) => {
        return event?.eventsInfo?.datepicker;
      })
      .sort((a, b) => {
        const dateA = parseDate(a.eventsInfo.datepicker);
        const dateB = parseDate(b.eventsInfo.datepicker);

        if (!dateA || isNaN(dateA.getTime())) return 1;
        if (!dateB || isNaN(dateB.getTime())) return -1;

        return dateA.getTime() - dateB.getTime();
      })
      .filter((event: IEvent) => {
        const rawEndDate =
          event.eventsInfo.datepickerEnd ?? event.eventsInfo.datepicker;
        const eventEndDate = parseDate(rawEndDate);

        if (!eventEndDate || isNaN(eventEndDate.getTime())) {
          return false;
        }

        const eventEndDateOnly = new Date(
          eventEndDate.getFullYear(),
          eventEndDate.getMonth(),
          eventEndDate.getDate()
        );

        const today = new Date();
        const todayOnly = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate()
        );

        return eventEndDateOnly >= todayOnly;
      });
  };

  const eventsData = removeOldEvents(useEventsData()).slice(0, 3);

  const renderNews = newsData?.map(
    ({ newsInfo: { tag }, uri, title, date }, index) => {
      return (
        <S.Card
          to={uri}
          key={index}
          className="newsCard"
          aria-label="Länk till nyhet"
        >
          <S.TextWrapper>
            <S.CardInfo>
              {tag && <Tag tag={tag} />}
              {date && <S.CardDetailsSmall>{date}</S.CardDetailsSmall>}
            </S.CardInfo>
            {title && <S.CardTitle>{title}</S.CardTitle>}
            <S.CardLink>Läs mer</S.CardLink>
          </S.TextWrapper>
        </S.Card>
      );
    }
  );

  const renderEvents = eventsData?.map(
    (
      {
        eventsInfo: {
          tag,
          image,
          location,
          startTime,
          endDate,
          datepicker,
          datepickerEnd,
        },
        uri,
        title,
      },
      index
    ) => {
      const imageData = toGatsbyImage(image);

      return (
        <S.Card to={uri} key={index} aria-label="Länk till händelse">
          {imageData && (
            <S.CardImage image={imageData} alt={image.altText || ""} />
          )}
          <S.TextWrapper>
            {tag && <Tag tag={tag} />}
            <S.CardInfo>
              {datepicker && (
                <S.CardDetails>
                  <CalendarIcon />
                  {formatDate(datepicker, false)}
                  {datepickerEnd && <>- {formatDate(datepickerEnd, false)}</>}
                </S.CardDetails>
              )}

              {startTime && (
                <S.CardDetails>
                  <ClockIcon />
                  {startTime}
                  {endDate && ` - ${endDate}`}
                </S.CardDetails>
              )}
              {location && (
                <S.CardDetails>
                  <MapPinIcon />
                  {location}
                </S.CardDetails>
              )}
            </S.CardInfo>
            {title && <S.CardTitle>{title}</S.CardTitle>}
          </S.TextWrapper>
        </S.Card>
      );
    }
  );

  return (
    <MainSection>
      <S.Inner>
        <S.BackgroundWrapper $bgColor={backgroundColors}>
          <S.LeftWrapper>
            <S.Title>Kalender</S.Title>
            <S.CardsWrapper>{renderEvents}</S.CardsWrapper>
            <S.ButtonWrapper>
              <Button
                link={{
                  url: "/verksamhet/kalender",
                  title: "Till kalendern",
                  target: "_self",
                }}
                type="textLink"
                arrow
              />
            </S.ButtonWrapper>
          </S.LeftWrapper>
        </S.BackgroundWrapper>
        <S.BackgroundWrapper $bgColor={backgroundColors}>
          <S.RightWrapper>
            <S.Title>Nyheter</S.Title>
            <S.CardsWrapper>{renderNews}</S.CardsWrapper>
            <S.ButtonWrapper>
              <Button
                link={{
                  url: "/nyheter",
                  title: "Se alla nyheter",
                  target: "_self",
                }}
                type="textLink"
                arrow
              />
            </S.ButtonWrapper>
          </S.RightWrapper>
        </S.BackgroundWrapper>
      </S.Inner>
    </MainSection>
  );
};

export default LatestStart;
